import { jwtDecode } from 'jwt-decode';

/** Session persistée sous une seule clé : un autre onglet la voit changer d'un bloc (événement storage) */
export const SESSION_STORAGE_KEY = 'teiji.session';
/** Ancienne clé (JWT seul, sans refresh token), supprimée au chargement */
const LEGACY_TOKEN_STORAGE_KEY = 'teiji.token';

/** Le JWT est rafraîchi avant une requête s'il expire dans moins de 30 s */
export const EXPIRATION_MARGIN_MS = 30_000;

/** Requêtes d'authentification : ni token ni refresh avant envoi */
const AUTH_PATHS = ['/login', '/token/refresh', '/logout', '/sso/okta/token'];

/** Claim d'identité du JWT (lexik user_id_claim, App\EventListener\JWTCreatedListener::OWNER_URN_CLAIM) */
export const OWNER_URN_CLAIM = 'owner.urn';

/**
 * Payload du JWT émis par l'API : uniquement l'identité technique. Nom, email, rôle et passwordDefined
 * ne sont pas dans le jeton : ils viennent de GET /users/me (useAuthStore.loadCurrentUser).
 */
export interface TokenPayload {
  /** URN plateforme de l'utilisateur ($:teiji:user:<id>) */
  [OWNER_URN_CLAIM]: Urn;
  exp: number;
  iat: number;
}

/** Utilisateur connecté, chargé depuis GET /users/me */
export interface AuthUser {
  urn: Urn;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  /** false : l'utilisateur doit définir un mot de passe local (ex. connecté en SSO), cf. RequireAuth */
  passwordDefined: boolean;
}

export interface Session {
  token: string;
  refreshToken: string | null;
  /** Timestamp (s, heure serveur) d'expiration du refresh token */
  refreshTokenExpiration: number | null;
  /** Avance de l'horloge serveur sur celle du navigateur (ms), mesurée avec l'iat du token à sa réception */
  clockOffset: number;
}

/** Réponse de /login, /token/refresh et /sso/okta/token */
export interface TokenResponse {
  token: string;
  refresh_token?: string;
  refresh_token_expiration?: number;
}

function decodeToken(token: string | null): TokenPayload | null {
  if (!token) return null;
  try {
    return jwtDecode<TokenPayload>(token);
  } catch {
    return null;
  }
}

/** URN de l'utilisateur porté par le token, ou null si le token est illisible */
export function getTokenUrn(token: string | null): Urn | null {
  return decodeToken(token)?.[OWNER_URN_CLAIM] ?? null;
}

/** Session construite à partir d'une réponse de /login ou /token/refresh */
export function createSession(response: TokenResponse, now = Date.now()): Session {
  const payload = decodeToken(response.token);
  if (!payload?.exp || !payload[OWNER_URN_CLAIM]) throw new Error('Token invalide');

  return {
    token: response.token,
    refreshToken: response.refresh_token || null,
    refreshTokenExpiration: response.refresh_token_expiration || null,
    clockOffset: payload.iat ? payload.iat * 1000 - now : 0,
  };
}

/**
 * Vrai tant que la session est utilisable : JWT valide, ou JWT expiré mais refresh token valide
 * (il sera rafraîchi à la prochaine requête).
 */
export function isSessionActive(session: Session | null, now = Date.now()): boolean {
  const payload = decodeToken(session?.token ?? null);
  if (!session || !payload?.exp || !payload[OWNER_URN_CLAIM]) return false;

  const serverNow = now + session.clockOffset;
  if (payload.exp * 1000 > serverNow) return true;

  return !!session.refreshToken && (session.refreshTokenExpiration ?? 0) * 1000 > serverNow;
}

/** Vrai si le JWT expire dans moins de EXPIRATION_MARGIN_MS (heure serveur estimée) */
export function isTokenExpiring(session: Session, now = Date.now()): boolean {
  const payload = decodeToken(session.token);
  if (!payload?.exp) return true;

  return payload.exp * 1000 - (now + session.clockOffset) < EXPIRATION_MARGIN_MS;
}

export function isAuthPath(path: string): boolean {
  const pathname = path.split('?')[0];
  return AUTH_PATHS.some((authPath) => pathname.endsWith(authPath));
}

/** Session stockée, ou null (et nettoyage) si absente, illisible ou terminée */
export function readStoredSession(): Session | null {
  localStorage.removeItem(LEGACY_TOKEN_STORAGE_KEY);

  let session: Session | null;
  try {
    session = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) ?? 'null') as Session | null;
  } catch {
    session = null;
  }

  if (session && isSessionActive(session)) return session;

  localStorage.removeItem(SESSION_STORAGE_KEY);
  return null;
}

export function storeSession(session: Session | null): void {
  if (session) {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  }
}
